import { RECORDING_FRAME_RATE } from './recording.constant'
import { ProctoringEventType, type ProctoringLeaveReason } from '@/constants/enums'
import { debugScreeningProctoringLog } from '@/utils/screening-proctoring-debug.util'
import { CAMERA_OFF_COUNTDOWN_SECONDS, FACE_COUNTDOWN_SECONDS, NO_FACE_MESSAGE, createAlert, multipleFacesMessage, pickActiveAlert } from './monitor-alert.util'
import type {
    InterviewStreamPlugin,
    InterviewStreamPluginContext,
    InterviewStreamPluginInput,
    InterviewStreamPluginRegistration,
    InterviewStreamState,
    MonitorAlert,
    MonitorAlertKind,
    RecordingInitData,
    RecordingInitOptions,
} from './interview-stream-host.type'
import { normalizeStreamPlugin } from './interview-stream-host.type'
import { createRecordingPlugin, type RecordingEvent, type RecordingPluginHandle, type RecordingStartedInfo } from './plugins/recording.plugin'

export type { RecordingEvent, RecordingStartedInfo }

export type InterviewStreamHostOptions = {
    plugins?: InterviewStreamPluginInput[]
    addProctoringEvent: (type: ProctoringEventType) => void
    onTerminate: (reason: ProctoringLeaveReason) => void
    /** When false, warnings still show but countdown timers and auto-leave are skipped. */
    enableProctoringCountdownLeave?: boolean
    /**
     * Alert kinds that start a countdown and auto-leave when enableProctoringCountdownLeave is true.
     * Defaults to all kinds (`no_face`, `multiple_faces`, `camera_off`).
     */
    countdownLeaveKinds?: MonitorAlertKind[]
    log: (message: string) => void
    onStateChange?: (state: InterviewStreamHostState) => void
}

export type InterviewStreamHostState = {
    activeAlert: MonitorAlert | null
    faceCount: number
    isScreenSharing: boolean
    hasLocalVideo: boolean
}

function emptyStreams(): InterviewStreamState {
    return {
        cameraStream: null,
        videoTrack: null,
        audioTrack: null,
        remoteAudioTrack: null,
        screenTrack: null,
        activeVideoTrack: null,
        videoElement: null,
    }
}

export class InterviewStreamHost {
    private streams: InterviewStreamState = emptyStreams()
    private pluginRegistrations: InterviewStreamPluginRegistration[] = []
    private runningPluginIds = new Set<string>()
    private recordingPlugin: RecordingPluginHandle
    private alerts: Partial<Record<MonitorAlertKind, MonitorAlert>> = {}
    private countdownTimers = new Map<MonitorAlertKind, ReturnType<typeof setInterval>>()
    private faceCount = 0
    private faceMonitoringPaused = false
    private started = false
    /**
     * Set once a countdown expires and the interview is being ended. From then on no warning or countdown re-opens —
     * otherwise the monitor's next "no face" tick started a fresh 30s countdown while the session was closing.
     */
    private terminating = false

    private readonly addProctoringEvent: (type: ProctoringEventType) => void
    private readonly onTerminate: (reason: ProctoringLeaveReason) => void
    private enableProctoringCountdownLeave: boolean
    private countdownLeaveKinds: Set<MonitorAlertKind>
    private readonly log: (message: string) => void
    private readonly onStateChange?: (state: InterviewStreamHostState) => void

    constructor(options: InterviewStreamHostOptions) {
        this.addProctoringEvent = options.addProctoringEvent
        this.onTerminate = options.onTerminate
        this.enableProctoringCountdownLeave = options.enableProctoringCountdownLeave !== false
        this.countdownLeaveKinds = new Set(options.countdownLeaveKinds ?? (['no_face', 'multiple_faces', 'camera_off'] satisfies MonitorAlertKind[]))
        this.log = options.log
        this.onStateChange = options.onStateChange
        this.recordingPlugin = createRecordingPlugin()
        this.pluginRegistrations = (options.plugins ?? []).map(normalizeStreamPlugin)
    }

    setProctoringCountdownLeave(enabled: boolean, kinds?: MonitorAlertKind[]) {
        this.enableProctoringCountdownLeave = enabled
        if (kinds) {
            this.countdownLeaveKinds = new Set(kinds)
        }
    }

    private getMonitorPlugins(): InterviewStreamPlugin[] {
        return this.pluginRegistrations.map((registration) => registration.plugin)
    }

    setPluginEnabled(pluginId: string, enabled: boolean) {
        const registration = this.pluginRegistrations.find((entry) => entry.plugin.id === pluginId)
        if (!registration || registration.enabled === enabled) {
            return
        }

        registration.enabled = enabled
        this.applyPluginEnabledState(registration)
    }

    isPluginEnabled(pluginId: string): boolean {
        const registration = this.pluginRegistrations.find((entry) => entry.plugin.id === pluginId)
        return registration?.enabled ?? false
    }

    syncPluginRegistrations(plugins: InterviewStreamPluginInput[]) {
        const nextRegistrations = plugins.map(normalizeStreamPlugin)
        const nextIds = new Set(nextRegistrations.map((entry) => entry.plugin.id))

        for (const registration of this.pluginRegistrations) {
            const next = nextRegistrations.find((entry) => entry.plugin.id === registration.plugin.id)
            if (!next || !next.enabled) {
                this.stopPluginIfRunning(registration.plugin)
            }
        }

        for (const registration of this.pluginRegistrations) {
            if (!nextIds.has(registration.plugin.id)) {
                this.stopPluginIfRunning(registration.plugin)
            }
        }

        this.pluginRegistrations = nextRegistrations

        if (!this.started) {
            return
        }

        const ctx = this.buildContext()
        for (const registration of this.pluginRegistrations) {
            this.applyPluginEnabledState(registration, ctx)
        }
    }

    private stopPluginIfRunning(plugin: InterviewStreamPlugin) {
        if (!this.runningPluginIds.has(plugin.id)) {
            return
        }
        plugin.stop()
        this.runningPluginIds.delete(plugin.id)
    }

    private applyPluginEnabledState(registration: InterviewStreamPluginRegistration, ctx = this.buildContext()) {
        if (!this.started) {
            return
        }

        if (registration.enabled) {
            if (!this.runningPluginIds.has(registration.plugin.id)) {
                void registration.plugin.start(ctx)
                this.runningPluginIds.add(registration.plugin.id)
            }
            registration.plugin.onStreamsUpdated?.(ctx)
            return
        }

        this.stopPluginIfRunning(registration.plugin)
    }

    getState(): InterviewStreamHostState {
        return {
            activeAlert: pickActiveAlert(this.alerts),
            faceCount: this.faceCount,
            isScreenSharing: Boolean(this.streams.screenTrack),
            hasLocalVideo: Boolean(this.streams.videoTrack),
        }
    }

    private suppressStateEmission = false

    private emitState() {
        if (this.suppressStateEmission) {
            return
        }
        this.onStateChange?.(this.getState())
    }

    private buildContext(): InterviewStreamPluginContext {
        return {
            streams: this.streams,
            addProctoringEvent: this.addProctoringEvent,
            requestTerminate: this.onTerminate,
            log: this.log,
            setFaceCount: (count) => {
                this.faceCount = count
                this.emitState()
            },
            showNoFaceWarning: (options) => this.showNoFaceWarning(options),
            showMultipleFacesWarning: (count) => this.showMultipleFacesWarning(count),
            clearFaceWarning: () => this.clearFaceWarning(),
            showCameraOffWarning: () => this.showCameraOffWarning(),
            clearCameraOffWarning: () => this.clearCameraOffWarning(),
            notifyScreenShareEnded: () => this.handleScreenShareEnded(),
            isVideoActive: () => this.isVideoActive(),
            isFaceMonitoringPaused: () => this.faceMonitoringPaused,
        }
    }

    private isVideoActive(): boolean {
        const track = this.streams.videoTrack
        return Boolean(track && track.readyState === 'live' && track.enabled && !track.muted)
    }

    private clearCountdown(kind: MonitorAlertKind) {
        const timer = this.countdownTimers.get(kind)
        if (timer) {
            clearInterval(timer)
            this.countdownTimers.delete(kind)
        }
        const alert = this.alerts[kind]
        if (alert) {
            this.alerts[kind] = { ...alert, countdownSeconds: null }
        }
    }

    private startCountdown(kind: MonitorAlertKind, seconds: number, onExpire: () => void) {
        if (this.terminating || !this.enableProctoringCountdownLeave || !this.countdownLeaveKinds.has(kind)) {
            return
        }

        if (this.countdownTimers.has(kind)) {
            return
        }

        let remaining = seconds
        const alert = this.alerts[kind]
        if (alert) {
            this.alerts[kind] = { ...alert, countdownSeconds: remaining }
            this.emitState()
        }

        const timer = setInterval(() => {
            remaining -= 1
            const current = this.alerts[kind]
            if (current) {
                this.alerts[kind] = { ...current, countdownSeconds: remaining }
                this.emitState()
            }

            if (remaining <= 0) {
                // End once: stop every countdown and warning, then hand over to the caller to close the session.
                this.terminating = true
                for (const running of [...this.countdownTimers.keys()]) {
                    this.clearCountdown(running)
                }
                this.alerts = {}
                this.emitState()
                onExpire()
            }
        }, 1000)

        this.countdownTimers.set(kind, timer)
    }

    private clearActiveFaceAlert(kind: 'no_face' | 'multiple_faces', { logFaceOk }: { logFaceOk: boolean }) {
        if (this.alerts[kind] == null) {
            return false
        }
        this.clearCountdown(kind)
        delete this.alerts[kind]
        if (logFaceOk) {
            this.addProctoringEvent(ProctoringEventType.FaceOk)
        }
        return true
    }

    private showNoFaceWarning({ logProctoring = false }: { logProctoring?: boolean } = {}) {
        if (this.terminating) return
        const alreadyActive = this.alerts.no_face != null
        // Close a multiple-faces episode before opening no-face so durations stay accurate.
        this.clearActiveFaceAlert('multiple_faces', { logFaceOk: true })
        this.faceCount = 0
        this.alerts.no_face = createAlert('no_face', NO_FACE_MESSAGE, 0)
        if (logProctoring && !alreadyActive) {
            this.addProctoringEvent(ProctoringEventType.NoFace)
            debugScreeningProctoringLog('host showNoFaceWarning', { logProctoring: true, alreadyActive })
        } else if (!alreadyActive) {
            debugScreeningProctoringLog('host showNoFaceWarning', { logProctoring: false, alreadyActive })
        }
        this.startCountdown('no_face', FACE_COUNTDOWN_SECONDS, () => this.onTerminate('face_undetected'))
        this.emitState()
    }

    private showMultipleFacesWarning(count: number) {
        if (this.terminating) return
        const alreadyActive = this.alerts.multiple_faces != null
        // Close a no-face episode before opening multiple-faces so durations stay accurate.
        this.clearActiveFaceAlert('no_face', { logFaceOk: true })
        this.faceCount = count
        this.alerts.multiple_faces = createAlert('multiple_faces', multipleFacesMessage(count), count)
        if (!alreadyActive) {
            this.addProctoringEvent(ProctoringEventType.MultipleFaces)
            debugScreeningProctoringLog('host showMultipleFacesWarning', { faceCount: count })
        }
        this.startCountdown('multiple_faces', FACE_COUNTDOWN_SECONDS, () => this.onTerminate('multiple_faces'))
        this.emitState()
    }

    private clearFaceWarning() {
        const clearedNoFace = this.clearActiveFaceAlert('no_face', { logFaceOk: true })
        const clearedMultiple = this.clearActiveFaceAlert('multiple_faces', { logFaceOk: !clearedNoFace })
        if (clearedNoFace || clearedMultiple) {
            debugScreeningProctoringLog('host clearFaceWarning', { clearedNoFace, clearedMultiple })
        }
        this.emitState()
    }

    private showCameraOffWarning() {
        if (this.terminating) return
        this.faceMonitoringPaused = true
        this.clearFaceWarning()
        this.alerts.camera_off = createAlert(
            'camera_off',
            'Please turn your camera back on immediately to continue the interview. If the camera stays off, this interview will be automatically terminated and marked as incomplete.'
        )
        this.addProctoringEvent(ProctoringEventType.CameraOff)
        debugScreeningProctoringLog('host showCameraOffWarning')
        this.startCountdown('camera_off', CAMERA_OFF_COUNTDOWN_SECONDS, () => this.onTerminate('camera_off'))
        this.emitState()
    }

    private clearCameraOffWarning() {
        const hadAlert = this.alerts.camera_off != null
        this.faceMonitoringPaused = false
        this.clearCountdown('camera_off')
        delete this.alerts.camera_off
        if (hadAlert) {
            debugScreeningProctoringLog('host clearCameraOffWarning')
        }
        this.emitState()
    }

    private notifyRecordingStreamsUpdated() {
        const ctx = this.buildContext()
        this.recordingPlugin.plugin.onStreamsUpdated?.(ctx)
    }

    private notifyPluginsUpdated() {
        this.notifyRecordingStreamsUpdated()
        if (!this.started) {
            return
        }
        const ctx = this.buildContext()
        for (const registration of this.pluginRegistrations) {
            if (!registration.enabled || !this.runningPluginIds.has(registration.plugin.id)) {
                continue
            }
            registration.plugin.onStreamsUpdated?.(ctx)
        }
    }

    private rebuildCameraStream() {
        const tracks = [this.streams.videoTrack, this.streams.audioTrack].filter((track): track is MediaStreamTrack => track != null)
        this.streams.cameraStream = tracks.length > 0 ? new MediaStream(tracks) : null
    }

    private bindPreview() {
        const el = this.streams.videoElement
        const track = this.streams.videoTrack
        if (!el) {
            return
        }
        if (!track) {
            if (el.srcObject) {
                el.srcObject = null
            }
            return
        }
        const current = el.srcObject
        if (current instanceof MediaStream && current.getVideoTracks()[0] === track) {
            return
        }
        const stream = new MediaStream([track])
        el.srcObject = stream
        el.play().catch(() => undefined)
    }

    setVideoElement(element: HTMLVideoElement | null) {
        if (this.streams.videoElement === element) {
            return
        }
        this.streams.videoElement = element
        this.bindPreview()
        this.notifyPluginsUpdated()
        this.emitState()
    }

    start() {
        if (this.started) {
            return
        }
        this.started = true
        this.terminating = false
        debugScreeningProctoringLog('host start', {
            pluginIds: this.pluginRegistrations.filter((r) => r.enabled !== false).map((r) => r.plugin.id),
            hasVideoElement: Boolean(this.streams.videoElement),
            hasVideoTrack: Boolean(this.streams.videoTrack),
            videoActive: this.isVideoActive(),
        })
        const ctx = this.buildContext()
        void this.recordingPlugin.plugin.start(ctx)

        for (const registration of this.pluginRegistrations) {
            if (!registration.enabled) {
                continue
            }
            void registration.plugin.start(ctx)
            this.runningPluginIds.add(registration.plugin.id)
        }
        this.emitState()
    }

    stop() {
        if (!this.started) {
            return
        }
        this.started = false
        debugScreeningProctoringLog('host stop')
        for (const kind of this.countdownTimers.keys()) {
            this.clearCountdown(kind)
        }
        this.alerts = {}
        for (const plugin of this.getMonitorPlugins()) {
            if (!this.runningPluginIds.has(plugin.id)) {
                continue
            }
            plugin.stop()
            this.runningPluginIds.delete(plugin.id)
        }
        this.emitState()
    }

    attachCameraStream(stream: MediaStream) {
        this.streams.videoTrack = stream.getVideoTracks()[0] ?? null
        this.streams.audioTrack = stream.getAudioTracks()[0] ?? null
        const track = this.streams.videoTrack
        debugScreeningProctoringLog('host attachCameraStream', {
            hasVideoTrack: Boolean(track),
            readyState: track?.readyState ?? null,
            enabled: track?.enabled ?? null,
            muted: track?.muted ?? null,
            videoActive: this.isVideoActive(),
            hostStarted: this.started,
            hasVideoElement: Boolean(this.streams.videoElement),
        })
        this.rebuildCameraStream()
        this.streams.activeVideoTrack = this.streams.screenTrack ?? this.streams.videoTrack
        this.bindPreview()
        this.notifyPluginsUpdated()
        this.emitState()
    }

    detachCameraStream() {
        this.streams.videoTrack = null
        this.streams.audioTrack = null
        this.rebuildCameraStream()
        if (!this.streams.screenTrack) {
            this.streams.activeVideoTrack = null
        }
        this.bindPreview()
        this.notifyPluginsUpdated()
        this.emitState()
    }

    replaceCameraTrack(videoTrack: MediaStreamTrack | null) {
        const previousTrack = this.streams.videoTrack
        this.streams.videoTrack = videoTrack
        this.rebuildCameraStream()
        this.streams.activeVideoTrack = this.streams.screenTrack ?? videoTrack
        this.bindPreview()
        this.notifyPluginsUpdated()
        this.emitState()

        if (previousTrack && previousTrack !== videoTrack && previousTrack.readyState === 'live') {
            previousTrack.stop()
        }
    }

    replaceAudioTrack(audioTrack: MediaStreamTrack | null) {
        this.streams.audioTrack = audioTrack
        this.rebuildCameraStream()
        this.notifyPluginsUpdated()
        this.emitState()
    }

    attachRemoteAudioStream(stream: MediaStream) {
        this.streams.remoteAudioTrack = stream.getAudioTracks()[0] ?? null
        this.notifyPluginsUpdated()
    }

    detachRemoteAudioStream() {
        this.streams.remoteAudioTrack = null
        this.notifyPluginsUpdated()
    }

    stopCameraTracks() {
        this.suppressStateEmission = true
        try {
            this.streams.cameraStream?.getTracks().forEach((track) => track.stop())
            if (this.streams.screenTrack) {
                this.streams.screenTrack.stop()
            }
            this.streams.screenTrack = null
            this.streams.activeVideoTrack = null
            this.streams.videoTrack = null
            this.streams.audioTrack = null
            this.streams.remoteAudioTrack = null
            this.rebuildCameraStream()
            this.bindPreview()
            this.notifyPluginsUpdated()
        } finally {
            this.suppressStateEmission = false
        }
    }

    private handleScreenShareEnded() {
        this.streams.screenTrack = null
        this.streams.activeVideoTrack = this.streams.videoTrack
        this.notifyPluginsUpdated()
        this.emitState()
    }

    async startScreenShare(): Promise<boolean> {
        if (!this.streams.videoTrack) {
            return false
        }
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: { ideal: RECORDING_FRAME_RATE } } })
        const screenTrack = screenStream.getVideoTracks()[0]
        if (!screenTrack) {
            return false
        }

        this.streams.screenTrack = screenTrack
        this.streams.activeVideoTrack = screenTrack
        this.notifyPluginsUpdated()
        this.emitState()
        return true
    }

    getScreenTrack(): MediaStreamTrack | null {
        return this.streams.screenTrack
    }

    setLocalMicMuted(muted: boolean) {
        this.recordingPlugin.setLocalMuted(muted)
    }

    async initializeRecording(options?: RecordingInitOptions): Promise<RecordingInitData> {
        const data = await this.recordingPlugin.initializeRecording(options)
        return data
    }

    setRecordingInit(data: RecordingInitData) {
        this.recordingPlugin.setInitData(data)
    }

    requestStartRecording() {
        void this.recordingPlugin.requestStart(this.buildContext())
    }

    tryStartRecording() {
        this.requestStartRecording()
    }

    scheduleRecordingFallback(delayMs: number) {
        this.recordingPlugin.scheduleFallback(delayMs, () => this.requestStartRecording())
    }

    cancelRecordingFallback() {
        this.recordingPlugin.cancelFallback()
    }

    setOnRecordingStarted(callback: ((info: RecordingStartedInfo) => void) | null) {
        this.recordingPlugin.setOnRecordingStarted(callback)
    }

    setOnRecordingEvent(callback: ((event: RecordingEvent) => void) | null) {
        this.recordingPlugin.setOnRecordingEvent(callback)
    }

    getRecordingStartedInfo(): RecordingStartedInfo | null {
        return this.recordingPlugin.getRecordingStartedInfo()
    }

    getMediaOffsetSeconds(): number | null {
        return this.recordingPlugin.getMediaOffsetSeconds()
    }

    async flushAndStopRecording() {
        await this.recordingPlugin.flushAndStop()
    }

    async stopAndFinalizeRecording() {
        await this.recordingPlugin.stopAndFinalize()
    }
}
